import { Stripe, StripeElements } from '@stripe/stripe-js';
import api from 'api';
import { PaymentDtoLevelEnum, StripePaymentSource, SubscribeDtoPeriodEnum, SubscribeDtoTypeEnum } from 'api/generated';
import { AxiosError } from 'axios';
import { useAsyncAction, useStripeChangePlan, useStripeSubscribeOrPurchase } from 'hooks';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { actions, selectors, useAppSelector } from 'store';
import { PaymentOptions } from 'store/ducks/courses/types';

interface Props {
  stripe: Stripe | null;
  elements: StripeElements | null;
  courseId?: string;
  promocode: string;
  period?: SubscribeDtoPeriodEnum;
  level?: PaymentDtoLevelEnum;
  source?: StripePaymentSource;
  isTrial?: boolean;
  useFirstMonthDiscount: boolean;
  isUpdate?: boolean;
  isCertificate: boolean | undefined;
  isPurchase?: boolean;
  courseName?: string;
  amount?: number;
  subscribeType: SubscribeDtoTypeEnum;
  setInvoiceId: Dispatch<SetStateAction<string>>;
  clientSecret: string | undefined;
  setClientSecret: Dispatch<SetStateAction<string | undefined>>;
  setTransactionId: Dispatch<SetStateAction<string | undefined>>;
  isActivate: boolean | undefined;
  onCouponDataReceived?: (couponData: any) => void;
  abId?: string;
  impactClickId?: string;
  googleClickId?: string;
}

type PurchaseParams = {
  courseId: string;
  promocode?: string;
  level: string;
  source: string;
};

type SubscribeParams = PurchaseParams & {
  period: number;
};

type InstallmentParams = PurchaseParams & {
  useFirstMonthDiscount: boolean;
};

export const useStripeDefault = ({
  stripe,
  elements,
  courseId,
  promocode,
  period,
  level,
  source,
  isCertificate,
  isTrial,
  isUpdate,
  isPurchase,
  courseName,
  useFirstMonthDiscount,
  amount,
  subscribeType,
  clientSecret,
  setInvoiceId,
  setClientSecret,
  setTransactionId,
  isActivate,
  onCouponDataReceived,
  abId,
  impactClickId,
  googleClickId,
}: Props) => {
  // @ts-ignore
  const [subscribeCourseAction] = useAsyncAction(actions.payments.subscribeCourse);
  // @ts-ignore
  const [purchaseCourseAction] = useAsyncAction(actions.payments.purchaseCourse);
  // NEW: Optimized two-phase actions for installment payments
  // @ts-ignore
  const [validateInstallmentPricingAction] = useAsyncAction(actions.payments.validateInstallmentPricing);
  // @ts-ignore
  const [createInstallmentPaymentAction] = useAsyncAction(actions.payments.createInstallmentPayment);

  const [errorMessage, setErrorMessage] = useState<string>();
  const latestRequestIdRef = useRef(0);

  const isLatestRequest = (requestId: number) => latestRequestIdRef.current === requestId;

  const paymentOption = useAppSelector(selectors.courses.selectPaymentOption);
  const authToken = useAppSelector(selectors.auth.selectAccessToken); // Get auth token

  // Check period values
  const periodValue = period as string | undefined;
  const periodValueLower = periodValue?.toLowerCase();
  const periodIsInstallment = periodValueLower === 'installment';
  const periodIsSubscription = periodValueLower === 'monthly' || periodValueLower === 'annual';
  const periodIsLifetime = periodValueLower === 'lifetime';
  const isCertificatePaymentPlan = isCertificate && paymentOption === PaymentOptions.PAYMENT_PLAN;

  // For one-time purchases (lifetime):
  // CRITICAL: Check period='lifetime' FIRST before anything else
  // This prevents lifetime from being detected as installment
  // Also, do NOT let a stale paymentOption force one-time when period is subscription/installment
  const isOneTime =
    (periodIsLifetime && !isCertificatePaymentPlan) ||
    (paymentOption === PaymentOptions.ONETIME && !periodIsSubscription && !periodIsInstallment) ||
    (isPurchase && !isCertificate);

  // For subscriptions, we have a period (monthly/annual) and isPurchase is false
  const isSubscription = !isPurchase && periodIsSubscription;

  // Installment can be detected by:
  // 1. Unauthorized flow: isPurchase=true, isCertificate=true, NOT one-time, NOT subscription, NOT lifetime
  // 2. Authorized flow: period='installment', isPurchase=false
  // CRITICAL: Must explicitly check that it's NOT lifetime
  const isInstallment =
    (isCertificatePaymentPlan && !periodIsSubscription) ||
    (!periodIsLifetime && // Explicitly exclude lifetime first
      ((isPurchase && isCertificate && !isOneTime && !isSubscription) || (!isPurchase && periodIsInstallment)));

  const isNonPurchaseUpdate = isUpdate && !isPurchase;

  const params = {
    elements,
    stripe,
    clientSecret,
    courseId,
    source,
    period,
  };

  const {
    handleSubmit: handleSubmitSubscribeOrPurchase,
    isLoading: isLoadingSubscribeOrPurchase,
    errorMessage: errorMessageSubscribeOrPurchase,
    isSuccess: isSuccessSubscribeOrPurchase,
  } = useStripeSubscribeOrPurchase({
    ...params,
    promocode,
    level,
    isTrial,
    isPurchase,
    courseName,
    amount,
    setTransactionId,
    isActivate,
  });

  const {
    handleSubmit: handleSubmitChangePlan,
    isLoading: isLoadingChangePlan,
    errorMessage: errorMessageChangePlan,
    isSuccess: isSuccessChangePlan,
  } = useStripeChangePlan({
    ...params,
    level: subscribeType,
    setTransactionId,
  });

  const onGetClientSecretSubscribeOrPurchase = async (requestId: number) => {
    const timestamp = new Date().toISOString();
    console.log(
      `🔵🔵🔵 [STRIPE-v2024-FIX-${timestamp}] onGetClientSecretSubscribeOrPurchase - NEW VERSION WITH LIFETIME FIX`,
    );
    console.log('🔵 [STRIPE] onGetClientSecretSubscribeOrPurchase called with:', {
      courseId,
      level,
      source,
      period,
      periodValue,
      periodValueLower,
      isPurchase,
      isCertificate,
      periodIsLifetime,
      periodIsInstallment,
      periodIsSubscription,
      paymentOption,
      isOneTime,
      isInstallment,
      isSubscription,
      promocode,
      useFirstMonthDiscount,
      timestamp,
    });

    // For installment purchases (certificate + not one-time), period is not required
    const isInstallmentPurchase = isInstallment;

    // Prevent mixed-flow checkout calls while course billing mode is still resolving.
    // Without this guard, certificate checkout can briefly route to one-time purchase.
    if (isPurchase && typeof isCertificate === 'undefined') {
      console.log('⏳ [STRIPE] Waiting for certificate/payment mode resolution before requesting client secret');
      return;
    }

    if (!courseId || !level || !source || (!period && !isInstallmentPurchase)) {
      console.log('🔴 [STRIPE] Early return: missing required params', {
        courseId: !!courseId,
        level: !!level,
        source: !!source,
        period: !!period,
        isInstallmentPurchase,
        isInstallment,
        isSubscription,
        isOneTime,
      });
      return;
    }
    const params = {
      courseId,
      promocode,
      level,
      source,
      ...(abId && { abId }),
      ...(impactClickId && { impactClickId }),
      ...(googleClickId && { googleClickId }),
    };

    const onResponse = async (
      action: (params: any) => Promise<any>,
      actionParams: SubscribeParams | InstallmentParams | PurchaseParams,
    ) => {
      try {
        console.log('🟡 [STRIPE] Calling payment API with params:', actionParams);
        // Pass useToken based on authToken presence
        const response = await action({ ...actionParams, useToken: !!authToken });
        console.log('🟢 [STRIPE] Payment API response received:', response);
        const { clientSecret, invoiceId, coupons } = response;

        if (!isLatestRequest(requestId)) {
          console.log('⚪ [STRIPE] Ignoring stale payment response', { requestId, latest: latestRequestIdRef.current });
          return;
        }

        console.log(
          '🟢 [STRIPE] Setting clientSecret:',
          clientSecret ? 'YES (length: ' + clientSecret.length + ')' : 'NO',
        );
        console.log('🟢 [STRIPE] Setting invoiceId:', invoiceId || 'NONE');
        console.log('🟢 [STRIPE] Coupons in response:', coupons || 'NONE');

        setClientSecret(clientSecret);
        setInvoiceId(invoiceId);

        // Notify parent component about coupon data
        if (onCouponDataReceived && coupons) {
          console.log('🟢 [STRIPE] Calling onCouponDataReceived with coupons:', coupons);
          onCouponDataReceived(coupons);
        } else {
          console.log(
            '⚪ [STRIPE] Not calling coupon callback. Has callback:',
            !!onCouponDataReceived,
            'Has coupons:',
            !!coupons,
          );
        }
      } catch (error) {
        if (!isLatestRequest(requestId)) {
          console.log('⚪ [STRIPE] Ignoring stale payment error', { requestId, latest: latestRequestIdRef.current });
          return;
        }
        console.error('🔴 [STRIPE] Payment API error:', error);
        console.error('🔴 [STRIPE] Error details:', {
          message: (error as AxiosError).message,
          response: (error as AxiosError).response?.data,
          status: (error as AxiosError).response?.status,
        });
        setErrorMessage((error as AxiosError).message);
      }
    };

    // SUBSCRIPTION: Monthly or annual subscriptions (period-driven priority)
    if (periodIsSubscription || isSubscription) {
      console.log('🟣 [STRIPE] Taking subscription path (period-driven)');
      const subscribeParams = { ...params, period };
      await onResponse(subscribeCourseAction, subscribeParams);
      return; // Exit early
    }

    // INSTALLMENT PAYMENT: Certificate purchase with installment option
    if (periodIsInstallment || isInstallment) {
      console.log('🟣 [STRIPE] Taking installment payment path (two-phase)', {
        isPurchase,
        isCertificate,
        isInstallment: isInstallment || periodIsInstallment,
        periodIsInstallment,
      });
      // OPTIMIZED: Use two-phase approach for installment payments
      try {
        console.log('📊 [STRIPE-PHASE-1] Validating pricing...');
        const validationParams = {
          courseId: params.courseId,
          promocode: params.promocode,
          source: params.source,
          level: params.level,
          useFirstMonthDiscount,
        };
        console.log('📊 [STRIPE-PHASE-1] Validation params:', JSON.stringify(validationParams, null, 2));

        const pricingData = await validateInstallmentPricingAction(validationParams);

        if (!isLatestRequest(requestId)) {
          console.log('⚪ [STRIPE-PHASE-1] Ignoring stale pricing response', {
            requestId,
            latest: latestRequestIdRef.current,
          });
          return;
        }

        console.log('✅ [STRIPE-PHASE-1] Pricing data received:', JSON.stringify(pricingData, null, 2));

        // Notify parent component about coupon data
        if (onCouponDataReceived && pricingData.coupons) {
          console.log('✅ [STRIPE-PHASE-1] Calling onCouponDataReceived with coupons:', pricingData.coupons);
          onCouponDataReceived(pricingData.coupons);
        } else {
          console.log(
            '⚪ [STRIPE-PHASE-1] Not calling coupon callback. Has callback:',
            !!onCouponDataReceived,
            'Has coupons:',
            !!pricingData.coupons,
          );
        }

        console.log('💳 [STRIPE-PHASE-2] Creating payment intent...');
        const paymentParams = {
          courseId: params.courseId,
          totalAmount: pricingData.totalAmount,
          monthlyAmount: pricingData.monthlyAmount,
          firstMonthAmount: pricingData.firstMonthAmount,
          // Continuity for the server-side recompute: phase 2 now re-derives the price with the
          // SAME inputs phase 1 used and rejects on mismatch, so the coupon and first-month flag
          // must travel with the request.
          promocode: params.promocode,
          useFirstMonthDiscount,
          source: params.source,
          level: params.level,
          useToken: !!authToken,
          ...(abId && { abId }),
          ...(impactClickId && { impactClickId }),
          ...(googleClickId && { googleClickId }),
        };
        console.log('💳 [STRIPE-PHASE-2] Payment params:', JSON.stringify(paymentParams, null, 2));

        const { clientSecret, invoiceId } = await createInstallmentPaymentAction(paymentParams);

        if (!isLatestRequest(requestId)) {
          console.log('⚪ [STRIPE-PHASE-2] Ignoring stale payment response', {
            requestId,
            latest: latestRequestIdRef.current,
          });
          return;
        }

        console.log('✅ [STRIPE-PHASE-2] Payment created:', {
          hasClientSecret: !!clientSecret,
          clientSecretLength: clientSecret?.length || 0,
          invoiceId: invoiceId || 'NONE',
        });

        setClientSecret(clientSecret || '');
        if (invoiceId) {
          setInvoiceId(invoiceId);
        }
        console.log('🎉 [STRIPE] Payment flow completed successfully');
      } catch (error) {
        if (!isLatestRequest(requestId)) {
          console.log('⚪ [STRIPE] Ignoring stale installment error', {
            requestId,
            latest: latestRequestIdRef.current,
          });
          return;
        }
        console.error('🔴 [STRIPE] Installment payment error:', error);
        console.error('🔴 [STRIPE] Error details:', {
          message: (error as AxiosError).message,
          response: (error as AxiosError).response?.data,
          status: (error as AxiosError).response?.status,
        });
        setErrorMessage((error as AxiosError).message);
      }
      return; // Exit early after installment flow
    }

    // CRITICAL: Check for LIFETIME (one-time purchase) after subscription/installment
    if (periodIsLifetime || isOneTime) {
      console.log('🟣 [STRIPE] Taking one-time purchase path (lifetime) - PRIORITY CHECK', {
        isPurchase,
        isCertificate,
        isOneTime,
        periodIsLifetime,
        periodValue,
      });
      console.log('🟣 [STRIPE] Purchase params being sent:', {
        courseId: params.courseId,
        level: params.level,
        source: params.source,
        promocode: params.promocode,
        hasPromocode: !!params.promocode,
      });
      await onResponse(purchaseCourseAction, params);
      return; // Exit early
    }

    // ONE-TIME PURCHASE: Regular purchase or one-time certificate (fallback check)
    if (isPurchase && !isCertificate) {
      console.log('🟣 [STRIPE] Taking one-time purchase path (non-certificate)', {
        isPurchase,
        isCertificate,
        isOneTime,
        periodIsLifetime,
      });
      await onResponse(purchaseCourseAction, params);
      return; // Exit early
    }

    // If we reach here, log a warning
    console.log('⚠️ [STRIPE] No payment path matched! Params:', {
      isPurchase,
      isCertificate,
      isOneTime,
      isInstallment,
      isSubscription,
      period,
    });

    console.log('🏁 [STRIPE] onGetClientSecretSubscribeOrPurchase completed');
  };

  const onGetClientSecretChangePlan = async (requestId: number) => {
    // For setup intents, the token is usually handled by the guard on the backend if needed
    // or it's a public endpoint. If this needs auth, ensure api.Stripe call handles it.
    // Assuming this call is correctly authenticated if needed.
    // If this call also needs explicit token passing, it should be { useToken: !!authToken }
    const { data } = await api.Stripe.stripeControllerCustomerSetupIntentSecret();

    if (!isLatestRequest(requestId)) {
      console.log('⚪ [STRIPE] Ignoring stale change-plan response', { requestId, latest: latestRequestIdRef.current });
      return;
    }

    setClientSecret(data.clientSecret || '');
  };

  useEffect(() => {
    const requestId = latestRequestIdRef.current + 1;
    latestRequestIdRef.current = requestId;

    console.log('⚡ [STRIPE-USEEFFECT] Triggered with:', {
      hasStripe: !!stripe,
      isUpdate,
      isPurchase,
      hasAuthToken: !!authToken,
      promocode,
      useFirstMonthDiscount,
      period,
      timestamp: new Date().toISOString(),
    });

    if (isUpdate && !isPurchase) {
      console.log('🔄 [STRIPE-USEEFFECT] Calling onGetClientSecretChangePlan (update mode)');
      setClientSecret(undefined);
      onGetClientSecretChangePlan(requestId);
    }

    if (stripe && (!isUpdate || isPurchase)) {
      console.log('🚀 [STRIPE-USEEFFECT] Stripe ready, calling onGetClientSecretSubscribeOrPurchase');
      setClientSecret(undefined);
      setInvoiceId('');
      onGetClientSecretSubscribeOrPurchase(requestId);
    } else if (!stripe) {
      console.log('⏳ [STRIPE-USEEFFECT] Waiting for Stripe to load...');
    }
  }, [
    stripe,
    authToken,
    promocode,
    useFirstMonthDiscount,
    period,
    level,
    courseId,
    source,
    isPurchase,
    isCertificate,
    paymentOption,
  ]);

  const isDisabled = !stripe || !clientSecret;

  return {
    clientSecretStripe: clientSecret,
    handleSubmitStripe: isNonPurchaseUpdate ? handleSubmitChangePlan : handleSubmitSubscribeOrPurchase,
    errorMessageStripe: errorMessage
      ? errorMessage
      : isNonPurchaseUpdate
      ? errorMessageChangePlan
      : errorMessageSubscribeOrPurchase,
    isLoadingStripe: isNonPurchaseUpdate ? isLoadingChangePlan : isLoadingSubscribeOrPurchase,
    isSuccessStripe: isNonPurchaseUpdate ? isSuccessChangePlan : isSuccessSubscribeOrPurchase,
    isDisabledStripe: isDisabled,
  };
};
